import { Link } from 'react-router-dom';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { ClaimActions } from '@/components/common/ClaimActions';
import { trustedPartnerDetailPath } from '@/lib/constants/routes';
import { formatDate } from '@/lib/utils/date.utils';
import { isPdfUrl } from '@/lib/utils/media.utils';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { PerkCard as PerkCardData } from '../api/trusted-partners.api';
import { TRUSTED_PARTNERS_STRINGS as S } from '../trusted-partners.constants';

interface PerkCardProps {
  perk: PerkCardData;
  /** Called when the member activates Claim Offer, after the browser has begun the navigation. */
  onClaim: (offerId: string) => void;
  /** Called when the member activates Contact Us, after the browser has begun the navigation. */
  onContact: (offerId: string) => void;
}

/**
 * One live offer (FR-30). The claim actions are real anchors (see `ClaimActions`),
 * so the browser opens the link or mail app natively; the click is counted
 * afterwards through `onClaim` / `onContact` and can never block the navigation.
 * View Partner, Claim Offer and Contact Us share one non-wrapping row (FR-117).
 */
export function PerkCard({ perk, onClaim, onContact }: PerkCardProps) {
  // The offer cover is cropped to 16:9 on upload. A PDF cover cannot be an image, so it is skipped.
  const coverUrl = perk.coverPhotoUrl && !isPdfUrl(perk.coverPhotoUrl) ? perk.coverPhotoUrl : null;

  return (
    <article className="flex flex-col gap-3 rounded-card border-t-4 border-yellow bg-white p-4 shadow-card">
      {coverUrl && <img src={coverUrl} alt="" className="aspect-video w-full rounded-card object-cover" />}
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-xs font-medium text-navy">
          <Avatar photoUrl={perk.partner.logoUrl ?? undefined} initials={initialsFromName(perk.partner.businessName)} size={40} />
          <span className="truncate">{perk.partner.businessName}</span>
        </span>
        {perk.expiresAt && (
          <Badge variant="danger" size="sm">
            {S.EXPIRES_PREFIX} {formatDate(perk.expiresAt)}
          </Badge>
        )}
      </div>
      <h3 className="text-p5 font-semibold text-navy">{perk.title}</h3>
      <p className="text-p6 text-slate">{perk.description}</p>
      {/* `@container`: ClaimActions collapses Contact Us to its icon by THIS row's width, not the viewport's. */}
      <div className="@container mt-auto flex flex-nowrap items-center justify-between gap-2 pt-2">
        <Link
          to={trustedPartnerDetailPath(perk.partner.entityId)}
          className="shrink-0 text-xs font-medium text-dkgreen underline-offset-2 hover:underline"
        >
          {S.VIEW_PARTNER}
        </Link>
        <ClaimActions
          claimLinkUrl={perk.claimLinkUrl}
          claimEmail={perk.claimEmail}
          onClaim={() => onClaim(perk.id)}
          onContact={() => onContact(perk.id)}
        />
      </div>
    </article>
  );
}
